import React, {useEffect} from 'react';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import * as Sentry from '@sentry/react';

import {useSafeNavigate as useNavigate} from './util/safeNavigate';
import {useAppSelector, useAppDispatch} from './state/hooks';
import {registerDevice} from './state/device';
import {LoadingContainer} from './LoadingContainer';
import {useBrowserId} from './BrowserId';

export type RegisteredClientProps = {
  children: React.ReactNode;
};

/**
 * Component that handles registering this browser app with the server.
 *
 * This step is required in order to send texts and receive sent-events from the server.
 */
export const RegisteredClient = (props: RegisteredClientProps) => {
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const browserId = useBrowserId();
  const loggedIn = useAppSelector((state) => state.auth.loggedIn);
  const userId = useAppSelector((state) => state.auth.userId);
  const registering = useAppSelector((state) => state.device.registering);
  const error = useAppSelector((state) => {
    const err = state.auth.error || state.device.error;
    return err ? new Error(err || 'unknown error') : null;
  });
  const token = useAppSelector((state) => state.auth.token);

  if (userId) {
    Sentry.setUser({id: userId});
  }

  // Redirect users if they aren't logged in.
  useEffect(() => {
    if (!loggedIn) {
      navigate('/login');
    }
  }, [loggedIn]);

  // Register the device if the user is logged in. If they aren't the query
  // will simply fail.
  useEffect(() => {
    if (loggedIn && userId) {
      // TODO(jnu): there's probably a cleaner ID to use than this "browserId."
      dispatch(registerDevice({userId, client, deviceId: browserId}));
    }
  }, [loggedIn, userId, browserId]);

  return (
    <LoadingContainer error={error} data={token} loading={registering}>
      {props.children}
    </LoadingContainer>
  );
};
